import type { ButtonProps } from '@chakra-ui/react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import React from 'react';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { NAV_DARK_FOCUS_RING } from '../layout/nav-dark';
import CounterBadge from './CounterBadge';
import NavTag from './components/NavTag';

interface NavLinkProps extends ButtonProps {
  icon?: React.ReactNode | ((isActive: boolean) => React.ReactNode); // Left icon (static or function of active state)
  label: string; // Label text
  to?: string; // Navigation path; omit for pure-action rows (e.g. group toggles)
  badge?: number; // Optional counter badge (hidden when 0/undefined)
  tag?: string; // Optional word badge sitting where the counter would (e.g. "New")
  rightChildren?: React.ReactNode; // Optional right children (e.g., chevron)
  onClick?: () => void; // Optional click handler; with onClick the row is a button, not a link
  isActive?: boolean; // Optional prop to manually control active state
  iconBoxSize?: string | number; // Optional icon hitbox size for compact menus
}

/**
 * One row of the dark menu. Inactive rows are near-white text on the charcoal;
 * the active row sits on a faint white wash with a hairline, flat, no shadow.
 * The icon inherits the row colour, so hover and active light both at once.
 */
const NavLink: React.FC<NavLinkProps> = ({
  icon,
  label,
  to,
  badge,
  tag,
  rightChildren,
  onClick,
  isActive: isActiveProp,
  iconBoxSize = '22px',
  ...props
}) => {
  const location = useLocation();

  // Use the isActive prop if provided, otherwise use the location matching
  const isActive =
    isActiveProp !== undefined ? isActiveProp : to ? location.pathname.startsWith(to) : false;

  // The row is always a real <button>, so onClick rows stay keyboard-operable.
  const content = (
    <Button
      variant="plain"
      onClick={onClick}
      px="10px"
      py={0}
      fontFamily="heading"
      fontSize="14.5px"
      fontWeight={isActive ? 600 : 500}
      width="full"
      letterSpacing="-0.01em"
      justifyContent="flex-start"
      border="1px solid"
      borderColor={isActive ? 'navDark.activeBorder' : 'transparent'}
      bg={isActive ? 'navDark.activeBg' : 'transparent'}
      color={isActive ? 'navDark.textStrong' : 'navDark.text'}
      _hover={{
        bg: isActive ? 'navDark.activeBg' : 'navDark.hoverBg',
        color: 'navDark.textStrong',
      }}
      _focusVisible={NAV_DARK_FOCUS_RING}
      transition="background-color .15s, color .15s"
      rounded="9px"
      h={(props.h || props.height) ?? '34px'}
      {...props}
    >
      <Flex align="center" justify={label ? 'space-between' : 'center'} width="full">
        <Flex align="center" gap="11px" minW={0}>
          {icon && (
            <Box
              minW={iconBoxSize}
              minH={iconBoxSize}
              w={iconBoxSize}
              h={iconBoxSize}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              {typeof icon === 'function' ? icon(isActive) : icon}
            </Box>
          )}
          {label && (
            <Text
              fontWeight={props.fontWeight}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {label}
            </Text>
          )}
        </Flex>
        {(badge || tag || rightChildren) && (
          <Flex align="center" gap="6px" flexShrink={0}>
            {badge ? <CounterBadge count={badge} /> : null}
            {/* A word where the counter would sit: the counter is numeric by
                construction, and a row can carry one or the other. */}
            {tag ? <NavTag>{tag}</NavTag> : null}
            {rightChildren && <Box>{rightChildren}</Box>}
          </Flex>
        )}
      </Flex>
    </Button>
  );

  if (!onClick && to) {
    return (
      <RouterNavLink to={to} key={location.pathname} style={{ width: '100%' }}>
        {content}
      </RouterNavLink>
    );
  }

  return content;
};

export default NavLink;
